import { Accordion, AccordionDetails, AccordionSummary, Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useCallback, useMemo } from "react";
import { dateUtils } from "../../../../../utils/DateUtils";
import { fadeInMixin } from "../../../../../common/cssMixins";
import { CustomBackdrop } from "../../../../../common/LoadingScreen";
import { DateInput } from "../../../../../common/inputs/ValidatedInputs";

const monthStyle = {
    borderLeft: "1px solid",
    borderBottom: "0px",
    borderColor: "divider",
}

const dayStyle = {
    borderLeft: "1px solid",
    borderBottom: "1px solid",
    borderTop: "0px",
    borderColor: "divider",
    textAlign: "center",
    width: '30px',
    minWidth: '30px',
    maxWidth: '30px',
    padding: '5px 5px !important'
}

const weekendStyle = {
    ...dayStyle,
    backgroundColor: "divider"
}

const cellStyle = (dayOfWeek) => {
    return isWeekend(dayOfWeek) ? weekendStyle : dayStyle;
};

const rowHeaderStyle = {
    whiteSpace: 'nowrap',
    fontWeight: 'bold',
    padding: '5px 10px',
}

const isWeekend = (dayOfWeek) => {
    return dayOfWeek === 'Sat' || dayOfWeek === 'Sun';
}

const TableCalendar = ({ room, plans, data, index, fetchNextPage, hasNextPage, isFetching, onStartDateChange }) => {
    const startDate = useMemo(() => new Date(data[0].from?.toDateString()), [data]);

    const createCalendarData = useCallback(() => {
        let calendarData = {};

        const currentDate = new Date(startDate.toDateString());

        for (const page of data) {
            const { availability, prices, to } = page;

            while (currentDate <= to) {
                let pricesByPlan = {};

                const day = {
                    ...dateUtils.getExtendedDate(currentDate),
                    availableUnits: availability[dateUtils.getDateString(currentDate)],
                    pricesByPlan,
                }

                const monthName = `${day.monthName} ${day.year}`;

                if (!calendarData[monthName]) {
                    calendarData[monthName] = [];
                }

                for (const plan of plans) {
                    const pricesForPlan = prices.find(entry => entry.planId === plan.id);

                    if (pricesForPlan) {
                        pricesByPlan[plan.id] = pricesForPlan.prices[day.dateString];
                    }
                }

                calendarData[monthName].push(day);

                currentDate.setDate(currentDate.getDate() + 1);
            }
        }

        return calendarData;
    }, [data, plans, startDate]);

    const onTableScroll = (evt) => {
        const table = evt.target;

        const xPosition = table.scrollLeft + table.offsetWidth;

        if (hasNextPage && xPosition >= table.scrollWidth - 10) {
            !isFetching && fetchNextPage();
        }
    }

    const calendarData = useMemo(() => createCalendarData(), [createCalendarData]);

    return <Accordion
        defaultExpanded={true}
        sx={{
            margin: "20px",
            '&.Mui-expanded': {
                margin: "20px !important"
            },
            ...fadeInMixin(index / 4),
        }}
    >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: "bold" }}>{room.name}</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '8px 8px 16px', display: 'flex', position: 'relative' }}>
            {isFetching && <CustomBackdrop />}
            <Table sx={{ height: 'fit-content', maxWidth: 'fit-content' }}>
                <TableHead>
                    <TableRow>
                        <TableCell height={'104px'} colSpan={2}><Box sx={{ height: '-webkit-fill-available' }}><StartDateSelector value={startDate} onChange={(date) => onStartDateChange(date, data)} /></Box></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow sx={{ borderBottom: '2px solid black' }}>
                        <TableCell colSpan={2} sx={{ ...rowHeaderStyle, textAlign: 'right', fontWeight: 'bold' }}>Available units</TableCell>
                    </TableRow>
                    {plans.map(plan => <PlanHeader key={plan.id} plan={plan} />)}
                </TableBody>
            </Table>
            <Box sx={{ overflowX: 'auto' }} onScroll={onTableScroll}>
                <Table sx={{ borderRight: "1px solid", borderRightColor: "divider" }}>
                    <TableHead>
                        <TableRow>
                            {Object.entries(calendarData).map(([month, days]) => (
                                <TableCell sx={monthStyle} colSpan={days.length} key={month}><Box sx={{ position: 'sticky', left: '0px', width: 'fit-content', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{month}</Box></TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            {Object.entries(calendarData).map(([month, days], index) => (
                                days.map((day) => <TableCell sx={{ ...cellStyle(day.dayOfWeek), padding: '16px 5px', whiteSpace: 'pre-line' }} key={day.dateString}>{`${day.dayOfWeek}\n${day.dayOfMonth}`}</TableCell>)
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow sx={{ borderBottom: '2px solid black' }}>
                            {Object.entries(calendarData).map(([month, days], index) => (
                                days.map((day) => <TableCell sx={{ ...cellStyle(day.dayOfWeek), backgroundColor: day.availableUnits > 0 ? '#81c784' : '#e57373' }} key={day.dateString}>{day.availableUnits}</TableCell>)
                            ))}
                        </TableRow>
                        {plans.map(plan => <PlanContent key={plan.id} plan={plan} calendarData={calendarData} />)}
                    </TableBody>
                </Table>
            </Box>
        </AccordionDetails>
    </Accordion>
}

const PlanHeader = ({ plan }) => {
    return <>
        <TableRow>
            <TableCell rowSpan={2} sx={rowHeaderStyle}>{plan.name}</TableCell>
            <TableCell sx={rowHeaderStyle}>Price</TableCell>
        </TableRow>
        <TableRow>
            <TableCell sx={rowHeaderStyle}>Min. LOS</TableCell>
        </TableRow>
    </>
}

const PlanContent = ({ plan, calendarData }) => {
    return <>
        <TableRow>
            {Object.entries(calendarData).map(([month, days], index) => (
                days.map((day) => <TableCell sx={{ ...cellStyle(day.dayOfWeek) }} key={day.dateString}>{day.pricesByPlan[plan.id]?.enabled ? day.pricesByPlan[plan.id].price : '-'}</TableCell>)
            ))}
        </TableRow>
        <TableRow>
            {Object.entries(calendarData).map(([month, days], index) => (
                days.map((day) => <TableCell sx={{ ...cellStyle(day.dayOfWeek) }} key={day.dateString}>{day.pricesByPlan[plan.id]?.enabled ? day.pricesByPlan[plan.id].minLos : '-'}</TableCell>)
            ))}
        </TableRow>
    </>
}

const StartDateSelector = ({ value, onChange }) => {
    return <DateInput
        id={'startDate'}
        value={value}
        onChange={onChange}
        label='Start date'
        size='small'
        textFieldProps={{
            size: 'small',
            sx: {
                width: '150px'
            }
        }}
    />;
}

export default TableCalendar;